import { ReactNode } from 'react';

import { DateValue } from '@internationalized/date';

import {
  LoanStatusT,
  ProductTypeDisplayT,
  ProductTypeT,
} from './loan';

export const PAYMENT_METHODS = {
  ACH: 'ach',
  DEBIT_CARD: 'debit_card',
} as const;
export type PaymentMethodT = typeof PAYMENT_METHODS[keyof typeof PAYMENT_METHODS];

export type PaymentReqDataT = {
  paymentAmount: number;
  paymentDate: DateValue;
  paymentMethod: PaymentMethodT;
};

export type PaymentPostDataT = {
  date: string;
  payment_amount: number;
  payment_method: PaymentMethodT;
};

export type PaymentSuccessTypeT = 'immediate_payment' | 'scheduled_payment';
export type PaymentSuccessDisplayT = 'complete' | 'scheduled';

export const paymentSuccessTypeToDisplayMap: Record<PaymentSuccessTypeT, PaymentSuccessDisplayT> = {
  immediate_payment: 'complete',
  scheduled_payment: 'scheduled',
};

export type PaymentSuccessResponseT = {
  success_type: PaymentSuccessTypeT;
};

export type PaymentErrorTypeT =
  | 'invalid_date'
  | 'invalid_amount'
  | 'immediate_payment_failure'
  | 'invalid_cfpb_date'
  | 'server_error'
  | string
  ;

export type PaymentErrorResponseT = {
  error_type: PaymentErrorTypeT;
  message?: string;
};

export type UsePaymentMessagingPropsT = {
  isAutopay: boolean;
  isAutopayPending: boolean;
  isMultiple: boolean;
  dueAmount?: number; // nextPayment.dueAmount
  dueDate?: DateValue; // nextPayment.dueDate
  fullPayoffAmount?: number;
  isFullPayoffDisabled?: boolean;
  isNextMinimumPaymentDisabled?: boolean;
  loanStatus?: LoanStatusT;
  originalDueAmount?: number; // nextPayment.originalDueAmount
  paymentAmount?: number; // formData.selectedPaymentAmount
  paymentDate?: DateValue; // formData.selectedPaymentDate
  productType?: ProductTypeT;
  scheduledAmount?: number; // aggSP.scheduledAmount
  scheduledDate?: DateValue; // aggSP.targetDate
};

export type UseMessageT = (props: UsePaymentMessagingPropsT) => React.ReactNode;

export type IsFullPayoffPaymentT = (
  props: Pick<UsePaymentMessagingPropsT, 'fullPayoffAmount' | 'isFullPayoffDisabled' | 'paymentAmount'>
) => boolean;

export type NcMakeAPaymentFormFieldsT = {
  fullPayoffAmount?: number;
  paymentAmount?: number;
  paymentDate?: DateValue;
  paymentMessaging?: ReactNode;
  paymentMethod?: PaymentMethodT;
};

export type PaymentDetailsT = Required<Omit<NcMakeAPaymentFormFieldsT, 'paymentMessaging'>> & {
  accountLast4: string;
  paymentOwner: string;
  paymentSource: string;
  productTypeDisplay: ProductTypeDisplayT;
  paymentMessaging?: ReactNode;
  routingLast4?: string;
  successType?: PaymentSuccessTypeT;
};

export type MakePaymentResponseT = Record<PaymentSuccessTypeT, 'complete' | 'scheduled'>;

// Request Cash Advance: estimated minimum payment
export type NcEstimatedMinimumPaymentT = {
  feeDue: string;
  minimumPayment: string;
  outstandingPrincipal: string;
  paymentDate: DateValue;
  principalDue: string;
};
